import { Nullable } from "../../../types/utils";
import { Area } from "../../entities/Area";
import { LeveledParam } from "../../entities/LeveledParam";
import { QuantiledParam } from "../../entities/QuantiledParam";
import { TimeStep } from "../../entities/TimeStep";
import client from "../client";
import { OrderCategory, QuoteProductLeg } from "./Order";

export interface IRealtimeOrderProductData {
  name: string;
  subset_id: number;
  legs?: Array<QuoteProductLeg>;
  use_step?: Array<{
    use: number;
    step: string;
  }>;
  use?: Array<number>;
  hindcast?: Nullable<Array<number>>;
  number?: Array<number>;
  area?: Array<Area>;
  param?: Array<string>;
  levelist?: Array<string>;
  grid?: string;
  step?: Array<string>;
  param_levelist?: Array<LeveledParam>;
  param_quantile?: Array<QuantiledParam>;
  time?: Array<number>;
  time_step?: Array<TimeStep>;
  fcmonth?: Array<number>;
  channel?: Array<number>;
  direction?: Array<number>;
  frequency?: Array<number>;
  location?: number;
}

export interface IEuCutOutProductData {
  param: Array<{
    value: string;
    subset_id: number;
  }>,
  high_frequency: boolean;
}

export type TOrderProductData<T> = T extends OrderCategory.REALTIME ?
  IRealtimeOrderProductData
  : T extends OrderCategory.EU_CUTOUT ? IEuCutOutProductData : never

export type TOrderProduct<T = OrderCategory> = {
  id: number;
  order_id: number;
  data: TOrderProductData<T>
}

const list = <T extends OrderCategory>(orderId: number): Promise<Array<TOrderProduct<T>>> =>
  client.get(`/order/${orderId}/product/`).then(response => response.data);

const create = <T extends OrderCategory>(
  orderId: number,
  productData: TOrderProductData<T>
): Promise<TOrderProduct<T>> =>
  client.post(`/order/${orderId}/product/`, { data: productData }).then(response => response.data);

const update = <T extends OrderCategory>(
  orderId: number,
  productId: number,
  productData: TOrderProductData<T>
): Promise<TOrderProduct<T>> =>
  client.put(`/order/${orderId}/product/${productId}/`, { data: productData }).then(response => response.data);

const fetch = <T extends OrderCategory>(
  orderId: number | string,
  productId: number | string
): Promise<TOrderProduct<T>> =>
  client.get(`/order/${orderId}/product/${productId}/`).then(response => response.data);

const duplicate = <T extends OrderCategory>(
  orderId: number | string,
  productId: number | string
): Promise<TOrderProduct<T>> =>
  client.put(`/order/${orderId}/product/${productId}/duplicate/`).then(response => response.data);

const remove = <T extends OrderCategory>(
  orderId: number | string,
  productId: number | string
): Promise<TOrderProduct<T>> =>
  client.delete(`/order/${orderId}/product/${productId}/`).then(response => response.data);

export {
  list,
  fetch,
  create,
  update,
  duplicate,
  remove
}
